import React, { useState, useEffect } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
// import { Helmet } from "react-helmet"; // Will add when package is installed
import ScrollAnimation from "react-animate-on-scroll";
import { EnhancedMarkdownRenderer } from "../utils/enhancedMarkdownParser";
import DynamicBlogLoader from "../utils/dynamicBlogLoader";
import ImageHelper from "../utils/imageHelper";
import DebugHelper from "../utils/debugHelper";
import HeaderStatic from "../components/layouts/HeaderStatic";
import Footer from "../components/layouts/Footer";

function BlogDetailPage() {
  const { slug } = useParams();
  const history = useHistory();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toc, setToc] = useState([]);
  const [activeSection, setActiveSection] = useState('');
  const [toggleMenu, setToggleMenu] = useState(false);

  useEffect(() => {
    loadBlogData();
  }, [slug]);

  // Debug and fix overlay issues
  useEffect(() => {
    // Start monitoring for problematic iframes
    const observer = DebugHelper.startMonitoring();
    
    // Create debug panel (press Ctrl+Shift+D to toggle)
    DebugHelper.createDebugPanel();
    
    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (blog) {
      loadRelatedBlogs();
      generateTOC();
      window.scrollTo(0, 0);
    }
  }, [blog]);

  const headerToggler = (e) => {
    e.preventDefault();
    setToggleMenu(!toggleMenu);
  };

  // Close menu when clicking on content
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest(".content")) {
        setToggleMenu(false);
      }
    };
    
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const loadBlogData = async () => {
    try {
      setLoading(true);
      const blogData = await DynamicBlogLoader.loadBlogBySlug(slug);
      
      if (!blogData) {
        setError('Không tìm thấy bài viết');
        return;
      }
      
      setBlog(blogData);
      setError(null);
    } catch (err) {
      setError('Lỗi khi tải bài viết');
      console.error('Error loading blog:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedBlogs = async () => {
    try {
      if (!blog) return;
      
      const allBlogs = await DynamicBlogLoader.loadAllBlogs();
      const related = allBlogs
        .filter(b => 
          b.slug !== blog.slug && 
          (b.metadata.category === blog.metadata.category || 
           b.metadata.tags.some(tag => blog.metadata.tags.includes(tag)))
        )
        .slice(0, 3);
      
      setRelatedBlogs(related);
    } catch (err) {
      console.error('Error loading related blogs:', err);
    }
  };

  const generateTOC = () => {
    if (!blog) return;
    
    const content = blog.content;
    const headers = [];
    const headerRegex = /^(#{1,6})\s*(.*)/gm;
    let match;
    
    while ((match = headerRegex.exec(content)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      
      headers.push({ level, text, id });
    }
    
    setToc(headers);
  };

  const formatDate = (dateString) => {
    return DynamicBlogLoader.formatDate(dateString);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Technology': '#667eea',
      'Business': '#f093fb',
      'Crypto': '#4ecdc4',
      'Startup': '#ff6b6b',
      'Tutorial': '#45b7d1',
      'Reviews': '#96ceb4'
    };
    return colors[category] || '#6c757d';
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = blog?.metadata.title || 'Blog post';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    // You could add a toast notification here
    alert('Link đã được sao chép!');
  };

  if (loading) {
    return (
      <>
        <HeaderStatic
          logoSource="/images/logo.svg"
          toggleMenu={toggleMenu}
          headerToggler={headerToggler}
        />
        <main className={toggleMenu ? "content open" : "content"}>
          <div className="blog-detail-page">
            <div className="container">
              <div className="loading-container">
                <div className="loading-spinner">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Đang tải...</span>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </main>
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <HeaderStatic
          logoSource="/images/logo.svg"
          toggleMenu={toggleMenu}
          headerToggler={headerToggler}
        />
        <main className={toggleMenu ? "content open" : "content"}>
          <div className="blog-detail-page">
            <div className="container">
              <div className="error-container text-center">
                <h2>Oops!</h2>
                <p>{error || 'Không tìm thấy bài viết'}</p>
                <button 
                  onClick={() => history.goBack()} 
                  className="btn btn-primary"
                >
                  Quay lại
                </button>
              </div>
            </div>
            <Footer />
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <HeaderStatic
        logoSource="/images/logo.svg"
        toggleMenu={toggleMenu}
        headerToggler={headerToggler}
      />
      <main className={toggleMenu ? "content open" : "content"}>
        <div className="blog-detail-page">
          {/* SEO Meta tags - will be added when react-helmet is installed */}

          <section className="blog-detail-main">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <ScrollAnimation
                animateIn="fadeInUp"
                animateOut="fadeInOut"
                animateOnce={true}
              >
                <article className="blog-article">
                  <div className="blog-header">
                    <div className="blog-breadcrumb">
                      <Link to="/">Trang chủ</Link>
                      <span className="separator">/</span>
                      <Link to="/bai-viet">Bài viết</Link>
                      <span className="separator">/</span>
                      <span>{blog.metadata.title}</span>
                    </div>
                    
                    <div className="blog-meta-header">
                      <span 
                        className="category-badge"
                        style={{ backgroundColor: getCategoryColor(blog.metadata.category) }}
                      >
                        {blog.metadata.category}
                      </span>
                      {blog.metadata.featured && (
                        <span className="featured-badge">
                          <i className="fas fa-star"></i> Nổi bật
                        </span>
                      )}
                    </div>
                    
                    <h1 className="blog-title">{blog.metadata.title}</h1>
                    
                    <div className="blog-meta">
                      <div className="meta-left">
                        <span className="author">
                          <i className="far fa-user"></i>
                          {blog.metadata.author}
                        </span>
                        <span className="date">
                          <i className="far fa-calendar-alt"></i>
                          {formatDate(blog.metadata.date)}
                        </span>
                        <span className="reading-time">
                          <i className="far fa-clock"></i>
                          {blog.readingTime} phút đọc
                        </span>
                      </div>
                      
                      <div className="meta-right">
                        <div className="share-buttons">
                          <span className="share-label">Chia sẻ:</span>
                          <button 
                            onClick={() => handleShare('facebook')}
                            className="share-btn facebook"
                            title="Chia sẻ Facebook"
                          >
                            <i className="fab fa-facebook-f"></i>
                          </button>
                          <button 
                            onClick={() => handleShare('twitter')}
                            className="share-btn twitter"
                            title="Chia sẻ Twitter"
                          >
                            <i className="fab fa-twitter"></i>
                          </button>
                          <button 
                            onClick={() => handleShare('linkedin')}
                            className="share-btn linkedin"
                            title="Chia sẻ LinkedIn"
                          >
                            <i className="fab fa-linkedin-in"></i>
                          </button>
                          <button 
                            onClick={copyToClipboard}
                            className="share-btn copy"
                            title="Sao chép link"
                          >
                            <i className="fas fa-link"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {blog.metadata.description && (
                      <div className="blog-description">
                        <p>{blog.metadata.description}</p>
                      </div>
                    )}
                    
                    {blog.metadata.tags && blog.metadata.tags.length > 0 && (
                      <div className="blog-tags">
                        {blog.metadata.tags.map(tag => (
                          <span key={tag} className="blog-tag">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="blog-image-hero">
                    <img 
                      src={blog.metadata.image} 
                      alt={blog.metadata.title}
                      onError={ImageHelper.createImageErrorHandler({
                        width: 800,
                        height: 400,
                        text: blog.metadata.title,
                        color: getCategoryColor(blog.metadata.category)
                      })}
                    />
                  </div>
                  
                  <div className="blog-content">
                    <EnhancedMarkdownRenderer 
                      content={blog.content}
                      className="blog-markdown"
                      showMetadata={false}
                    />
                  </div>
                </article>
              </ScrollAnimation>
              
              {relatedBlogs.length > 0 && (
                <ScrollAnimation
                  animateIn="fadeInUp"
                  animateOut="fadeInOut"
                  animateOnce={true}
                  delay={200}
                >
                  <section className="related-blogs">
                    <h3>Bài viết liên quan</h3>
                    <div className="row">
                      {relatedBlogs.map(relatedBlog => (
                        <div key={relatedBlog.id} className="col-md-4">
                          <div className="related-blog-card">
                            <Link to={`/bai-viet/${relatedBlog.slug}`}>
                              <img 
                                src={relatedBlog.metadata.image} 
                                alt={relatedBlog.metadata.title}
                                onError={ImageHelper.createImageErrorHandler({
                                  width: 300,
                                  height: 150,
                                  text: relatedBlog.metadata.category,
                                  color: getCategoryColor(relatedBlog.metadata.category)
                                })}
                              />
                            </Link>
                            <div className="card-content">
                              <h4>
                                <Link to={`/bai-viet/${relatedBlog.slug}`}>
                                  {relatedBlog.metadata.title}
                                </Link>
                              </h4>
                              <p>{relatedBlog.metadata.description || relatedBlog.excerpt}</p>
                              <div className="card-meta">
                                <span>{formatDate(relatedBlog.metadata.date)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                </ScrollAnimation>
              )}
            </div>
            
            <div className="col-lg-4">
              <ScrollAnimation
                animateIn="fadeInRight"
                animateOut="fadeInOut"
                animateOnce={true}
                delay={300}
              >
                <aside className="blog-sidebar">
                  {toc.length > 0 && (
                    <div className="sidebar-widget toc-widget">
                      <h4>Mục lục</h4>
                      <ul className="toc-list">
                        {toc.map((item, index) => (
                          <li 
                            key={index} 
                            className={`toc-item level-${item.level} ${
                              activeSection === item.id ? 'active' : ''
                            }`}
                          >
                            <a href={`#${item.id}`}>
                              {item.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="sidebar-widget">
                    <h4>Theo dõi</h4>
                    <div className="social-links">
                      <a href="#" className="social-link facebook">
                        <i className="fab fa-facebook-f"></i>
                        Facebook
                      </a>
                      <a href="#" className="social-link youtube">
                        <i className="fab fa-youtube"></i>
                        YouTube
                      </a>
                      <a href="#" className="social-link telegram">
                        <i className="fab fa-telegram"></i>
                        Telegram
                      </a>
                    </div>
                  </div>
                </aside>
              </ScrollAnimation>
            </div>
          </div>
            </div>
          </section>
          
          <Footer />
        </div>
      </main>
    </>
  );
}

export default BlogDetailPage;